import { LayoutSizeContext } from 'App';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { WidgetVisibilityContext } from 'components/Widgets/WidgetVisibilityContext';
import { usePerms } from 'contexts/PermsContext';
import {
  canWidgetUsePopout,
  PopoutErrorBoundary,
  useWidgetPopout,
  WidgetPopoutPlaceholder,
  WidgetWindowPortal,
} from 'features/dashboard/features/WidgetPopout';
import React, {
  startTransition,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { IconContext } from 'react-icons';
import { FaExpandAlt, FaExternalLinkAlt, FaLock, FaLockOpen, FaTrash } from 'react-icons/fa';
import Popup from 'reactjs-popup';
import './Widget.css';

// A popped-out widget lives in its own window, so it is always "visible" —
// the dashboard viewport observer must not pause its polling/animations.
const POPPED_OUT_WIDGET_VISIBILITY = {
  isVisible: true,
  hasEnteredViewport: true,
  pollingActive: true,
};

export default function Widget({
  item,
  index,
  expandable,
  list,
  onListChange,
  removeWidget,
  dashboardPreview = false,
  uninteractable,
  scale,
  lockedFromRole = false,
  gridRowHeight,
  showResizeIndicator = false,
  allowDeleteInPreview = false,
  isPopoutEnabled = false,
}) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('Widget');
    }
  } catch (e) {}

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const widgetShellRef = useRef(null);
  const shouldLazyMountContent = !dashboardPreview;
  const [isWidgetInViewport, setIsWidgetInViewport] = useState(() => !shouldLazyMountContent);
  const [hasWidgetEnteredViewport, setHasWidgetEnteredViewport] = useState(
    () => !shouldLazyMountContent,
  );
  const perms = usePerms();
  const roleLocked = lockedFromRole === true;
  const canOverrideLockedFromRole = perms.testPermission([11, 12, 13], []);
  const isRoleLockedAndNotOverridable = roleLocked && !canOverrideLockedFromRole;
  const isLocked = Boolean(item?.locked) || isRoleLockedAndNotOverridable;
  const canShowDeleteUI =
    (!dashboardPreview || allowDeleteInPreview) &&
    !uninteractable &&
    perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']) &&
    typeof removeWidget === 'function';
  const showDeleteButton = canShowDeleteUI && !isLocked;

  const layout = useContext(LayoutSizeContext);
  const canPopOutWidget =
    isPopoutEnabled &&
    !dashboardPreview &&
    !uninteractable &&
    typeof item?.component === 'function' &&
    canWidgetUsePopout(item);
  const {
    isPoppedOut,
    isOpening,
    popoutWindow,
    popOutWidget,
    returnToDashboard,
    focusPopoutWindow,
  } = useWidgetPopout({ isEnabled: canPopOutWidget });
  const dashboardContentContainerRef = useRef(null);
  const [widgetContentElement] = useState(() => {
    const element = document.createElement('div');
    element.className = 'overflow-x-auto w-100 h-100 d-flex flex-column';
    element.style.minWidth = '0';
    element.style.minHeight = '0';
    return element;
  });
  const widgetIconSize = 10;
  const widgetHeaderFontSize = '0.9375rem';
  const widgetSubtextFontSize = '0.75rem';
  const widgetHeaderForegroundColor = 'var(--color-primary-foreground)';
  const widgetIconButtonStyle = {
    padding: 0,
    lineHeight: 1,
    color: widgetHeaderForegroundColor,
  };
  const widgetIconContextValue = {
    color: widgetHeaderForegroundColor,
    size: widgetIconSize,
    style: {
      fill: widgetHeaderForegroundColor,
      color: widgetHeaderForegroundColor,
    },
  };
  const scaleMult = layout?.scaleMult || 1;
  const hasGridRowOverride = Number.isFinite(gridRowHeight) && gridRowHeight > 0;
  const gridVerticalMargin = 10;
  const effectiveSingleHeight = hasGridRowOverride ? gridRowHeight : layout.widgetHeight;
  const effectiveDoubleHeight = hasGridRowOverride
    ? gridRowHeight * 2 + gridVerticalMargin
    : layout.doubleWidgetHeight;
  const getHeightForRowSpan = (rowSpan) => {
    const rows = Math.max(1, Number(rowSpan) || 1);
    if (hasGridRowOverride) {
      return gridRowHeight * rows + gridVerticalMargin * (rows - 1);
    }
    if (rows === 1) return layout.widgetHeight;
    if (rows === 2) return layout.doubleWidgetHeight;
    return layout.widgetHeight * rows + gridVerticalMargin * (rows - 1);
  };
  const effectiveWidgetHeight = getHeightForRowSpan(item?.h);
  const baseDoubleHeight = layout.doubleWidgetHeight / scaleMult;
  const softenedDoubleHeight = baseDoubleHeight * (1 + (scaleMult - 1) * 0.5);
  const shouldMountWidgetContent = !shouldLazyMountContent || hasWidgetEnteredViewport;

  useLayoutEffect(() => {
    if (
      shouldMountWidgetContent &&
      !isPoppedOut &&
      dashboardContentContainerRef.current &&
      widgetContentElement.parentNode !== dashboardContentContainerRef.current
    ) {
      dashboardContentContainerRef.current.appendChild(widgetContentElement);
    }
  }, [isPoppedOut, shouldMountWidgetContent, widgetContentElement]);

  useEffect(() => {
    if (!shouldLazyMountContent) {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    const node = widgetShellRef.current;
    if (!node || typeof window === 'undefined') {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries?.[0];
        if (!entry) {
          return;
        }

        const visible = entry.isIntersecting || entry.intersectionRatio > 0;
        setIsWidgetInViewport(visible);
        if (visible) {
          // Mounting a widget's content (especially a Leaflet map) is expensive.
          // Mark it as a non-urgent transition so it does not block scroll frames
          // while the user is scrolling; it lands once the thread is free.
          startTransition(() => setHasWidgetEnteredViewport(true));
        }
      },
      {
        root: null,
        rootMargin: '320px 0px 320px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [shouldLazyMountContent]);

  const widgetVisibilityValue = useMemo(
    () => ({
      isVisible: isWidgetInViewport,
      hasEnteredViewport: hasWidgetEnteredViewport,
      pollingActive: dashboardPreview ? true : isWidgetInViewport,
    }),
    [dashboardPreview, hasWidgetEnteredViewport, isWidgetInViewport],
  );
  const widgetRuntime = useMemo(
    () => ({
      hostElement: widgetContentElement,
      isPoppedOut,
      popoutWindow,
      runtimeWindow: popoutWindow || window,
      supportsFullscreen: !isPoppedOut || popoutWindow !== window.documentPictureInPicture?.window,
    }),
    [isPoppedOut, popoutWindow, widgetContentElement],
  );

  // JS shim for browsers without :has(): toggle a class on the widget root
  // so the CSS fallback (.fusion-widget-shell.handle-hover) can trigger the lift.
  const addHandleHover = () => {
    const node = widgetShellRef.current;
    if (node && !node.classList.contains('handle-hover')) node.classList.add('handle-hover');
  };

  const removeHandleHover = () => {
    const node = widgetShellRef.current;
    if (node && node.classList.contains('handle-hover')) node.classList.remove('handle-hover');
  };

  // Let the browser skip rendering/painting a widget's contents while it is
  // scrolled off-screen. The shell keeps an explicit height, and we hand the
  // browser an intrinsic-size hint so the scroll height stays stable (no
  // collapse/jump). This cuts paint/compositing cost when scrolling a tall
  // dashboard full of live maps. Only for real on-dashboard widgets.
  const enableContentVisibility = !dashboardPreview && !uninteractable;
  const intrinsicHeightPx = Number.isFinite(effectiveWidgetHeight)
    ? Math.round(effectiveWidgetHeight)
    : null;
  const contentVisibilityStyle =
    enableContentVisibility && intrinsicHeightPx
      ? { contentVisibility: 'auto', containIntrinsicSize: `auto ${intrinsicHeightPx}px` }
      : {};

  return (
    <div
      ref={widgetShellRef}
      className="fusion-widget-shell user-select-none position-relative d-flex flex-column p-2 pt-1 rounded-2"
      style={
        dashboardPreview
          ? {
              backgroundColor: 'var(--color-base)',
              height: `${100}%`,
              width: `${100}%`,
              '--widget-header-font-size': widgetHeaderFontSize,
              '--widget-subtext-font-size': widgetSubtextFontSize,
            }
          : {
              background: 'var(--color-base)',
              height: effectiveWidgetHeight,
              '--widget-header-font-size': widgetHeaderFontSize,
              '--widget-subtext-font-size': widgetSubtextFontSize,
              minHeight:
                item.w === 1 && item.h === 2
                  ? hasGridRowOverride
                    ? effectiveDoubleHeight
                    : softenedDoubleHeight
                  : item.w === 1 && item.h === 1
                    ? hasGridRowOverride
                      ? effectiveSingleHeight
                      : layout.widgetWidth
                    : undefined,
              ...contentVisibilityStyle,
            }
      }
    >
      {showResizeIndicator && !dashboardPreview && !uninteractable && (
        <div
          aria-hidden
          className="position-absolute"
          style={{
            right: 3,
            bottom: 3,
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 0 7px 7px',
            borderColor: 'transparent transparent rgba(255, 255, 255, 0.88) transparent',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}
      {uninteractable && (
        <div
          className="position-absolute start-0 end-0 bottom-0"
          style={{ top: 65, zIndex: 'calc(var(--z-dropdown) + 1)' }}
        />
      )}
      <div className="fusion-widget-header d-flex w-100 justify-content-between align-items-center">
        {/* Widget Display Name and Tooltip */}
        {
          <Popup
            trigger={
              // Widget Title
              <div
                className={`fw-bold overflow-hidden text-truncate text-nowrap ${item.isDraggable ? 'handle cursor-grab' : ''}`}
                style={{
                  fontSize: 'var(--widget-header-font-size)',
                  minWidth: '25%',
                }}
                onMouseEnter={item.isDraggable ? addHandleHover : undefined}
                onMouseLeave={item.isDraggable ? removeHandleHover : undefined}
                onTouchStart={item.isDraggable ? addHandleHover : undefined}
                onTouchEnd={item.isDraggable ? removeHandleHover : undefined}
              >
                {item.displayName || '\u00A0'}
              </div>
            }
            on="hover"
            position="top center"
            closeOnDocumentClick
          >
            {/* Widget Display Name Tooltip */}
            <div
              className="p-2 user-select-none rounded-3"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
              }}
            >
              {item.displayName}
            </div>
          </Popup>
        }
        {/* Widget Controls */}
        <div className="d-flex align-items-center gap-1">
          {/* Pop out to own window (desktop only) */}
          {canPopOutWidget && !isPoppedOut && (
            <Button
              variant="link"
              className="btn-transparent p-0"
              style={widgetIconButtonStyle}
              onClick={popOutWidget}
              disabled={isOpening}
              aria-busy={isOpening}
              aria-label={`Open ${item.displayName || 'widget'} in its own window`}
            >
              <IconContext.Provider value={widgetIconContextValue}>
                <FaExternalLinkAlt />
              </IconContext.Provider>
            </Button>
          )}
          {/* Lock */}
          {perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']) &&
            !uninteractable && (
              <Button
                variant="link"
                className="btn-transparent p-0"
                disabled={roleLocked && !canOverrideLockedFromRole}
                onClick={() => {
                  if (roleLocked && !canOverrideLockedFromRole) return;
                  const temp = [...list];
                  const current = temp[index] || {};
                  //If locked is undefined, we consider it unlocked, so we set it to true
                  const newLocked = current.locked !== undefined ? !current.locked : true;
                  temp[index] = {
                    ...current,
                    isDraggable: !newLocked,
                    locked: newLocked,
                  };
                  onListChange(temp);
                }}
                style={{
                  ...widgetIconButtonStyle,
                  cursor: roleLocked && !canOverrideLockedFromRole ? 'not-allowed' : undefined,
                  opacity: roleLocked && !canOverrideLockedFromRole ? 0.5 : 1,
                }}
              >
                {item.isDraggable ? (
                  <IconContext.Provider value={widgetIconContextValue}>
                    <FaLockOpen />
                  </IconContext.Provider>
                ) : (
                  <IconContext.Provider value={widgetIconContextValue}>
                    <FaLock />
                  </IconContext.Provider>
                )}
              </Button>
            )}
          {!dashboardPreview &&
            !uninteractable &&
            item.sidebarData.href &&
            item.sidebarData.href != '' && (
              <Button
                variant="link"
                className="btn-transparent p-0"
                style={widgetIconButtonStyle}
                onClick={() => {
                  if (!expandable || !item.sidebarData.href) return;

                  window.open(`${item.sidebarData.href}`, '_blank', 'noreferrer');
                }}
              >
                <IconContext.Provider value={widgetIconContextValue}>
                  <FaExpandAlt />
                </IconContext.Provider>
              </Button>
            )}
          {canShowDeleteUI && (
            <Button
              variant="link"
              className={`btn-transparent p-0 ${!showDeleteButton ? 'invisible' : ''}`}
              style={widgetIconButtonStyle}
              onClick={() => showDeleteButton && setShowDeleteConfirm(true)}
              aria-hidden={!showDeleteButton}
              tabIndex={showDeleteButton ? 0 : -1}
              disabled={!showDeleteButton}
            >
              <IconContext.Provider value={widgetIconContextValue}>
                <FaTrash />
              </IconContext.Provider>
            </Button>
          )}
        </div>
      </div>

      {typeof removeWidget === 'function' && (
        <ConfirmationModal
          show={showDeleteConfirm}
          handleShow={() => setShowDeleteConfirm(false)}
          bodyTxt={`Are you sure you want to delete ${item.displayName || 'this widget'}?`}
          cancelBtnTxt="Cancel"
          confirmBtnTxt="Delete"
          cancelHandler={() => setShowDeleteConfirm(false)}
          confirmHandler={() => {
            removeWidget(index);
            setShowDeleteConfirm(false);
          }}
        />
      )}

      {/* Widget Content */}
      <div
        ref={dashboardContentContainerRef}
        className="overflow-x-auto w-100 h-100"
        style={{ minWidth: 0, minHeight: 0 }}
      >
        {!shouldMountWidgetContent ? (
          <div
            className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary"
            style={{ opacity: 0.8 }}
          >
            Loading widget...
          </div>
        ) : isPoppedOut ? (
          <WidgetPopoutPlaceholder
            widgetName={item.displayName}
            onReturnToWidget={returnToDashboard}
            onFocusWindow={focusPopoutWindow}
          />
        ) : null}
      </div>

      {shouldMountWidgetContent &&
        createPortal(
          <PopoutErrorBoundary
            isPoppedOut={isPoppedOut}
            onReturn={isPoppedOut ? returnToDashboard : undefined}
          >
            <WidgetVisibilityContext.Provider
              value={isPoppedOut ? POPPED_OUT_WIDGET_VISIBILITY : widgetVisibilityValue}
            >
              {item.component(item.w, item.h, dashboardPreview, widgetRuntime)}
            </WidgetVisibilityContext.Provider>
          </PopoutErrorBoundary>,
          widgetContentElement,
        )}

      {/* The persistent content element moves between documents while its
          React portal remains mounted, preserving widget-local state. */}
      {isPoppedOut && (
        <WidgetWindowPortal
          title={item.displayName || 'Widget'}
          popoutWindow={popoutWindow}
          contentElement={widgetContentElement}
          dashboardContainer={dashboardContentContainerRef.current}
          onRequestReturn={returnToDashboard}
        />
      )}
    </div>
  );
}
